import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import UserService from '../../services/UserService';
import BoatService from '../../services/BoatService';
import './Admin.css';

const UserManagement = () => {
  const { token, isAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [passwordResetData, setPasswordResetData] = useState({
    userId: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: 'operator'
  });

  // Fetch users and boats on component mount
  useEffect(() => {
    fetchUsers();
    fetchBoats();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await UserService.getAllUsers(token);
      setUsers(data);
      setError(null);
    } catch (err) {
      setError('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoats = async () => {
    try {
      const data = await BoatService.getBoatsRegistry(token);
      setBoats(data);
    } catch (err) {
      console.error('Failed to load boats:', err);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordResetChange = (e) => {
    setPasswordResetData({
      ...passwordResetData,
      [e.target.name]: e.target.value
    });
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      confirmPassword: '',
      name: '',
      role: 'operator'
    });
  };

  const resetPasswordForm = () => {
    setPasswordResetData({
      userId: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const userData = {
        username: formData.username,
        password: formData.password,
        name: formData.name,
        role: formData.role
      };

      await UserService.createUser(token, userData);
      fetchUsers();
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      setError('Failed to add user: ' + err.message);
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setFormData({
      id: user._id,
      username: user.username,
      name: user.name,
      role: user.role,
      // Password fields left empty for editing
      password: '',
      confirmPassword: ''
    });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const { id, username, password, confirmPassword, ...updateData } = formData;

      await UserService.updateUser(token, id, updateData);
      fetchUsers();
      setEditingUser(null);
      resetForm();
    } catch (err) {
      setError('Failed to update user: ' + err.message);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (passwordResetData.newPassword !== passwordResetData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await UserService.updateUserPassword(token, passwordResetData.userId, {
        password: passwordResetData.newPassword
      });
      setShowResetPassword(false);
      resetPasswordForm();
      setError(null);
      alert('Password reset successful');
    } catch (err) {
      setError('Failed to reset password: ' + err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser?.id) {
      alert('You cannot delete your own account');
      return;
    }

    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await UserService.deleteUser(token, userId);
        fetchUsers();
      } catch (err) {
        setError('Failed to delete user: ' + err.message);
      }
    }
  };

  const handleAssignBoat = async (userId, boatId) => {
    try {
      await BoatService.assignBoatToUser(token, boatId, userId);
      fetchUsers();
      alert('Boat assigned successfully');
    } catch (err) {
      setError('Failed to assign boat: ' + err.message);
    }
  };

  const handleUnassignBoat = async (userId, boatId) => {
    try {
      await BoatService.unassignBoatFromUser(token, boatId, userId);
      fetchUsers();
      alert('Boat unassigned successfully');
    } catch (err) {
      setError('Failed to unassign boat: ' + err.message);
    }
  };

  if (!isAdmin()) {
    return <div className="admin-error">Admin access required</div>;
  }

  return (
    <div className="admin-container">
      <h2>उपयोगकर्ता प्रबंधन | User Management</h2>
      
      {error && <div className="admin-error">{error}</div>}
      
      <div className="admin-actions">
        <button 
          className="admin-button" 
          onClick={() => {
            resetForm();
            setShowAddForm(true);
            setEditingUser(null);
          }}
        >
          नया उपयोगकर्ता जोड़ें | Add New User
        </button>
        <button 
          className="admin-button" 
          onClick={() => {
            resetPasswordForm();
            setShowResetPassword(true);
          }}
        >
          पासवर्ड रीसेट करें | Reset Password
        </button>
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || editingUser) && (
        <div className="admin-form-container">
          <h3>
            {editingUser
              ? 'उपयोगकर्ता अपडेट करें | Update User'
              : 'नया उपयोगकर्ता जोड़ें | Add New User'}
          </h3>
          <form
            className="admin-form"
            onSubmit={editingUser ? handleUpdateUser : handleAddUser}
          >
            <div className="form-group">
              <label htmlFor="username">यूजरनेम | Username:</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                disabled={!!editingUser}
              />
            </div>
            {!editingUser && (
              <>
                <div className="form-group">
                  <label htmlFor="password">पासवर्ड | Password:</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editingUser}
                    minLength="6"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">
                    पासवर्ड की पुष्टि | Confirm Password:
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required={!editingUser}
                    minLength="6"
                  />
                </div>
              </>
            )}
            <div className="form-group">
              <label htmlFor="name">नाम | Name:</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="role">भूमिका | Role:</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                required
              >
                <option value="operator">ऑपरेटर | Operator</option>
                <option value="admin">प्रशासक | Admin</option>
              </select>
            </div>

            <div className="admin-form-buttons">
              <button type="submit" className="admin-button">
                {editingUser ? 'अपडेट | Update' : 'जोड़ें | Add'}
              </button>
              <button
                type="button"
                className="admin-button cancel"
                onClick={() => {
                  setEditingUser(null);
                  setShowAddForm(false);
                  resetForm();
                }}
              >
                रद्द करें | Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reset Password Form */}
      {showResetPassword && (
        <div className="admin-form-container">
          <h3>पासवर्ड रीसेट | Reset Password</h3>
          <form className="admin-form" onSubmit={handleResetPassword}>
            <div className="form-group">
              <label htmlFor="userId">उपयोगकर्ता | User:</label>
              <select
                id="userId"
                name="userId"
                value={passwordResetData.userId}
                onChange={handlePasswordResetChange}
                required
              >
                <option value="">उपयोगकर्ता चुनें | Select User</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.name} ({user.username})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">नया पासवर्ड | New Password:</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordResetData.newPassword}
                onChange={handlePasswordResetChange}
                required
                minLength="6"
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">
                पासवर्ड की पुष्टि | Confirm Password:
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordResetData.confirmPassword}
                onChange={handlePasswordResetChange}
                required
                minLength="6"
              />
            </div>

            <div className="admin-form-buttons">
              <button type="submit" className="admin-button">
                पासवर्ड रीसेट करें | Reset Password
              </button>
              <button
                type="button"
                className="admin-button cancel"
                onClick={() => {
                  setShowResetPassword(false);
                  resetPasswordForm();
                }}
              >
                रद्द करें | Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      <div className="admin-table-container">
        <h3>उपयोगकर्ताओं की सूची | User List</h3>
        {loading ? (
          <p>Loading...</p>
        ) : users.length === 0 ? (
          <p>कोई उपयोगकर्ता नहीं मिला | No users found</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>यूजरनेम | Username</th>
                <th>नाम | Name</th>
                <th>भूमिका | Role</th>
                <th>नावें | Boats</th>
                <th>कार्य | Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id}>
                  <td>{user.username}</td>
                  <td>{user.name}</td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role === 'admin'
                        ? 'प्रशासक | Admin'
                        : 'ऑपरेटर | Operator'}
                    </span>
                  </td>
                  <td>
                    <div className="assigned-boats">
                      {user.assignedBoats && user.assignedBoats.length > 0 ? (
                        <div>
                          <strong>असाइन की गई नावें | Assigned Boats:</strong>
                          <ul className="boat-list">
                            {user.assignedBoats.map((boatId) => {
                              const boat = boats.find(b => b.boatId === boatId);
                              return (
                                <li key={boatId} className="boat-item">
                                  <span className="boat-id">{boatId}</span>
                                  {boat && <span className="boat-name">({boat.displayName})</span>}
                                  <button
                                    className="action-button mini delete"
                                    onClick={() => handleUnassignBoat(user._id, boatId)}
                                    title="Unassign boat"
                                  >
                                    X
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : (
                        <span>कोई नाव नहीं | No boats assigned</span>
                      )}
                      
                      {boats.length > 0 && (
                        <div className="assign-boat-form">
                          <select
                            id={`assign-boat-${user._id}`}
                            onChange={(e) => {
                              if (e.target.value) {
                                handleAssignBoat(user._id, e.target.value);
                                e.target.value = '';  // Reset select
                              }
                            }}
                            defaultValue=""
                          >
                            <option value="">नाव असाइन करें | Assign boat...</option>
                            {boats
                              .filter(boat => !user.assignedBoats?.includes(boat.boatId))
                              .map(boat => (
                                <option key={boat.boatId} value={boat.boatId}>
                                  {boat.displayName} ({boat.boatId})
                                </option>
                              ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="action-buttons">
                    <button
                      className="action-button edit"
                      onClick={() => handleEditClick(user)}
                    >
                      संपादित करें | Edit
                    </button>
                    {user._id !== currentUser?.id && (
                      <button
                        className="action-button delete"
                        onClick={() => handleDeleteUser(user._id)}
                      >
                        हटाएं | Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default UserManagement; 